import React, { useMemo, useState } from "react";
import useSWR, { mutate as swrMutate } from "swr";
import api from "@/lib/api";
import { ddmmyyyy } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  CalendarClock, ChevronLeft, ChevronRight, AlertTriangle, Trash2, Loader2, Plus, CalendarDays,
} from "lucide-react";

const fetcher = (url) => api.get(url).then((r) => r.data);

const STATUS_STYLE = {
  work:     { bg: "bg-emerald-100 text-emerald-800 border-emerald-200", short: "W" },
  off:      { bg: "bg-gray-100 text-gray-600 border-gray-200",           short: "Off" },
  holiday:  { bg: "bg-blue-100 text-blue-800 border-blue-200",           short: "Hol" },
  sick:     { bg: "bg-amber-100 text-amber-800 border-amber-200",        short: "Sick" },
  training: { bg: "bg-purple-100 text-purple-800 border-purple-200",     short: "Trn" },
};

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const startOfWeek = (d) => {
  const dd = new Date(d);
  const day = (dd.getDay() + 6) % 7;      // Mon=0
  dd.setDate(dd.getDate() - day);
  dd.setHours(0, 0, 0, 0);
  return dd;
};

const iso = (d) => {
  const dd = new Date(d);
  const y = dd.getFullYear();
  const m = String(dd.getMonth() + 1).padStart(2, "0");
  const day = String(dd.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const AssignmentPill = ({ assignment, vehicles, hasConflict, onEdit }) => {
  const s = STATUS_STYLE[assignment.status] || STATUS_STYLE.work;
  const veh = vehicles?.find((v) => v.id === assignment.vehicle_id);
  return (
    <button type="button" onClick={() => onEdit(assignment)}
            data-testid={`assignment-${assignment.id}`}
            className={`w-full text-left rounded-md border px-1.5 py-1 mb-1 text-[10px] leading-tight ${s.bg} ${
              hasConflict ? "ring-2 ring-red-400 ring-offset-1" : ""
            }`}>
      <div className="font-semibold uppercase tracking-wider">{s.short}</div>
      {veh && (
        <div className="font-mono text-[9px] truncate opacity-80">{veh.registration}</div>
      )}
      {assignment.route && (
        <div className="font-mono text-[9px] truncate opacity-70">{assignment.route}</div>
      )}
    </button>
  );
};

const AssignmentDialog = ({ open, onOpenChange, cellCtx, drivers, vehicles, existing, onSaved }) => {
  const editing = !!existing;
  const [form, setForm] = useState({
    status: existing?.status || "work",
    vehicle_id: existing?.vehicle_id || "__none__",
    route: existing?.route || "",
    notes: existing?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    if (open) {
      setForm({
        status: existing?.status || "work",
        vehicle_id: existing?.vehicle_id || "__none__",
        route: existing?.route || "",
        notes: existing?.notes || "",
      });
    }
  }, [open, existing]);

  const driver = drivers?.find((d) => d.id === (existing?.driver_id || cellCtx?.driverId));

  const save = async () => {
    setBusy(true);
    try {
      const body = {
        status: form.status,
        vehicle_id: form.vehicle_id === "__none__" ? null : form.vehicle_id,
        route: form.route || null,
        notes: form.notes || null,
      };
      if (editing) {
        await api.patch(`/work-plan/${existing.id}`, body);
        toast.success("Assignment updated");
      } else {
        await api.post("/work-plan", {
          ...body,
          driver_id: cellCtx.driverId,
          work_date: cellCtx.dateIso,
        });
        toast.success("Assignment created");
      }
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setBusy(false);
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm("Delete this assignment?")) return;
    setBusy(true);
    try {
      await api.delete(`/work-plan/${existing.id}`);
      toast.success("Assignment removed");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="assignment-dialog">
        <DialogHeader>
          <DialogTitle>
            {editing ? "Edit assignment" : "New assignment"}
          </DialogTitle>
          <DialogDescription>
            {driver?.full_name} · {ddmmyyyy(existing?.work_date || cellCtx?.dateIso)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="assignment-status" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.keys(STATUS_STYLE).map((s) => (
                  <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Vehicle</Label>
            <Select value={form.vehicle_id} onValueChange={(v) => setForm((f) => ({ ...f, vehicle_id: v }))}>
              <SelectTrigger data-testid="assignment-vehicle" className="mt-1.5"><SelectValue placeholder="No vehicle" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— No vehicle —</SelectItem>
                {(vehicles || []).map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.registration}{v.make ? ` · ${v.make}` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Route / notes</Label>
            <Input value={form.route} onChange={(e) => setForm((f) => ({ ...f, route: e.target.value }))}
                    placeholder="R101, R204…" data-testid="assignment-route" className="mt-1.5 font-mono" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          {editing && (
            <Button variant="ghost" onClick={remove} disabled={busy}
                     className="text-red-600 mr-auto"
                     data-testid="assignment-delete">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={busy}
                   className="bg-brand-red hover:bg-brand-red-hover"
                   data-testid="assignment-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
            {editing ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const WorkPlanner = () => {
  const [anchor, setAnchor] = useState(iso(new Date()));
  const [dragVehicleId, setDragVehicleId] = useState(null);
  const [dropTargetKey, setDropTargetKey] = useState(null);

  const weekStart = useMemo(() => startOfWeek(anchor), [anchor]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return iso(d);
  }), [weekStart]);

  const rangeUrl = `/work-plan?anchor=${anchor}&view=week`;
  const { data: planData, mutate: reloadPlan } = useSWR(rangeUrl, fetcher);
  const { data: drivers } = useSWR("/drivers", fetcher);
  const { data: vehicles } = useSWR("/vehicles", fetcher);

  const [dialog, setDialog] = useState({ open: false, existing: null, cellCtx: null });

  const assignmentsByCell = useMemo(() => {
    const map = {};
    (planData?.assignments || []).forEach((a) => {
      const key = `${a.driver_id}|${a.work_date}`;
      (map[key] = map[key] || []).push(a);
    });
    return map;
  }, [planData]);

  const conflictIds = useMemo(() => {
    const ids = new Set();
    (planData?.conflicts || []).forEach((c) => (c.assignment_ids || []).forEach((id) => ids.add(id)));
    return ids;
  }, [planData]);

  const goto = (deltaDays) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + deltaDays);
    setAnchor(iso(d));
  };

  const activeDrivers = (drivers || []).filter((d) => d.status !== "inactive");

  const onDropCell = async (driverId, dateIso) => {
    const vid = dragVehicleId;
    setDragVehicleId(null);
    setDropTargetKey(null);
    if (!vid) return;
    // If an assignment already exists in this cell, PATCH it; else POST.
    const cellItems = assignmentsByCell[`${driverId}|${dateIso}`] || [];
    try {
      if (cellItems.length > 0) {
        await api.patch(`/work-plan/${cellItems[0].id}`, { vehicle_id: vid });
      } else {
        await api.post("/work-plan", {
          driver_id: driverId,
          work_date: dateIso,
          status: "work",
          vehicle_id: vid,
        });
      }
      toast.success("Vehicle assigned");
      reloadPlan();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-5">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand-red font-mono">Operations</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight text-brand-text mt-1">Work Planner</h1>
          <p className="text-sm text-brand-text-muted mt-1 max-w-2xl">
            Schedule drivers to vehicles and routes. Conflicts (double-booked driver / vehicle,
            or a scheduled shift on an approved holiday) are highlighted in red — the planner is
            forward-looking and never modifies your DPD payroll data.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => goto(-7)} data-testid="week-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="text-xs font-mono px-3 py-1.5 rounded-lg border border-brand-border bg-white min-w-[180px] text-center"
               data-testid="week-range">
            {ddmmyyyy(weekDays[0])} — {ddmmyyyy(weekDays[6])}
          </div>
          <Button variant="outline" size="sm" onClick={() => goto(7)} data-testid="week-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setAnchor(iso(new Date()))}
                   data-testid="week-today">
            <CalendarDays className="w-3.5 h-3.5 mr-1.5" /> Today
          </Button>
        </div>
      </div>

      {planData?.conflicts && planData.conflicts.length > 0 && (
        <Card className="p-3 rounded-2xl border-red-200 bg-red-50" data-testid="conflicts-banner">
          <div className="flex items-center gap-2 text-red-800 font-semibold text-xs uppercase tracking-wider">
            <AlertTriangle className="w-3.5 h-3.5" />
            {planData.conflicts.length} conflict{planData.conflicts.length === 1 ? "" : "s"} this week
          </div>
          <ul className="mt-2 space-y-1 text-[11px] text-red-800">
            {planData.conflicts.slice(0, 6).map((c, i) => (
              <li key={i} className="flex items-center gap-2" data-testid={`conflict-${c.kind}-${i}`}>
                <Badge variant="outline" className="text-[9px] uppercase bg-white border-red-200 text-red-700">
                  {c.kind.replace(/_/g, " ")}
                </Badge>
                {c.message}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* Vehicle drag palette */}
      {(vehicles || []).length > 0 && (
        <div className="rounded-2xl border border-brand-border bg-white px-3 py-2.5"
             data-testid="vehicle-drag-strip">
          <div className="text-[10px] uppercase tracking-wider text-brand-text-muted mb-1.5">
            Drag a vehicle onto a driver&rsquo;s day →
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(vehicles || []).map((v) => (
              <div key={v.id}
                   draggable
                   onDragStart={() => setDragVehicleId(v.id)}
                   onDragEnd={() => { setDragVehicleId(null); setDropTargetKey(null); }}
                   data-testid={`vehicle-chip-${v.id}`}
                   className={`select-none cursor-grab active:cursor-grabbing rounded-full border px-2.5 py-1 text-[10px] font-mono transition ${
                     dragVehicleId === v.id
                       ? "bg-brand-navy text-white border-brand-navy scale-95"
                       : "bg-white text-brand-text border-brand-border hover:bg-brand-surface"
                   }`}>
                {v.registration}{v.make ? ` · ${v.make.slice(0, 8)}` : ""}
              </div>
            ))}
          </div>
        </div>
      )}

      <Card className="rounded-2xl border-brand-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="work-planner-grid">
            <thead className="bg-brand-surface text-brand-text-muted uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-3 py-2 sticky left-0 bg-brand-surface z-10 min-w-[180px]">Driver</th>
                {weekDays.map((d, i) => (
                  <th key={d} className="text-left px-3 py-2 min-w-[130px]">
                    <div className="font-semibold text-brand-text">{DAY_NAMES[i]}</div>
                    <div className="font-mono text-[10px] text-brand-text-muted">{ddmmyyyy(d)}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-border">
              {activeDrivers.map((driver) => (
                <tr key={driver.id} data-testid={`row-${driver.id}`}>
                  <td className="px-3 py-2 sticky left-0 bg-white z-10 border-r border-brand-border">
                    <div className="font-medium text-brand-text truncate">{driver.full_name}</div>
                    <div className="text-[10px] font-mono text-brand-text-muted">{driver.driver_code}</div>
                  </td>
                  {weekDays.map((d) => {
                    const cellItems = assignmentsByCell[`${driver.id}|${d}`] || [];
                    const cellKey = `${driver.id}|${d}`;
                    const isDropTarget = dragVehicleId && dropTargetKey === cellKey;
                    return (
                      <td key={d}
                          className={`px-1.5 py-1.5 align-top transition ${isDropTarget ? "bg-brand-navy/10 outline outline-2 outline-brand-navy/40" : ""}`}
                          data-testid={`cell-${driver.id}-${d}`}
                          onDragOver={(e) => {
                            if (!dragVehicleId) return;
                            e.preventDefault();
                            if (dropTargetKey !== cellKey) setDropTargetKey(cellKey);
                          }}
                          onDragLeave={() => {
                            if (dropTargetKey === cellKey) setDropTargetKey(null);
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            onDropCell(driver.id, d);
                          }}>
                        {cellItems.map((a) => (
                          <AssignmentPill key={a.id} assignment={a} vehicles={vehicles}
                                          hasConflict={conflictIds.has(a.id)}
                                          onEdit={(existing) => setDialog({ open: true, existing, cellCtx: null })} />
                        ))}
                        <button type="button"
                                onClick={() => setDialog({ open: true, existing: null,
                                                             cellCtx: { driverId: driver.id, dateIso: d } })}
                                data-testid={`add-cell-${driver.id}-${d}`}
                                className="w-full text-brand-text-muted hover:text-brand-text hover:bg-brand-surface rounded-md border border-dashed border-brand-border px-1 py-0.5 text-[10px]">
                          <Plus className="w-3 h-3 inline" /> Add
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {activeDrivers.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-brand-text-muted">
                  <CalendarClock className="w-6 h-6 mx-auto mb-2 opacity-40" />
                  No active drivers to schedule.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <AssignmentDialog
        open={dialog.open}
        onOpenChange={(o) => setDialog((s) => ({ ...s, open: o }))}
        cellCtx={dialog.cellCtx}
        existing={dialog.existing}
        drivers={drivers}
        vehicles={vehicles}
        onSaved={() => { reloadPlan(); swrMutate(rangeUrl); }}
      />
    </div>
  );
};

export default WorkPlanner;
